import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function PaymentModal({ invoice, backendUrl = '', onClose, onSuccess }) {
  const [method, setMethod]       = useState(null); // null | 'card' | 'zelle'
  const [cfg, setCfg]             = useState(null);
  const [cardNum, setCardNum]     = useState('');
  const [expMonth, setExpMonth]   = useState('');
  const [expYear, setExpYear]     = useState('');
  const [cvv, setCvv]             = useState('');
  const [tipMode, setTipMode]     = useState('none'); // 'none' | '15' | '20' | 'custom'
  const [customTip, setCustomTip] = useState('');
  const [status, setStatus]       = useState('idle'); // idle | processing | success | error
  const [errorMsg, setErrorMsg]   = useState('');
  const [copied, setCopied]       = useState(false);

  const invoiceAmt = parseFloat(invoice.outstanding_amount || 0);

  const tipAmt = (() => {
    if (tipMode === '15') return Math.round(invoiceAmt * 15) / 100;
    if (tipMode === '20') return Math.round(invoiceAmt * 20) / 100;
    if (tipMode === 'custom') return Math.max(0, parseFloat(customTip) || 0);
    return 0;
  })();
  const chargeTotal = invoiceAmt + tipAmt;

  // Fetch Accept.js config when card method selected
  useEffect(() => {
    if (method !== 'card') return;
    let cancelled = false;
    axios.get(`${backendUrl}/api/payments/client-config`)
      .then(res => { if (!cancelled) setCfg(res.data); })
      .catch(() => { if (!cancelled) setErrorMsg('Could not load payment config. Please try again.'); });
    return () => { cancelled = true; };
  }, [method, backendUrl]);

  const handleCharge = () => {
    if (!window.Accept) {
      setErrorMsg('Payment processor failed to load. Please refresh the page and try again.');
      return;
    }
    if (!cfg) {
      setErrorMsg('Payment configuration not loaded. Please refresh and try again.');
      return;
    }
    const num = cardNum.replace(/\s/g, '');
    if (!num || !expMonth || !expYear || !cvv) {
      setErrorMsg('Please fill in all card fields.');
      return;
    }
    setErrorMsg('');
    setStatus('processing');

    try {
      window.Accept.dispatchData(
        {
          authData: { apiLoginID: cfg.apiLoginID, clientKey: cfg.clientKey },
          cardData: { cardNumber: num, month: expMonth, year: expYear, cardCode: cvv },
        },
        async (response) => {
          try {
            if (response.messages.resultCode === 'Error') {
              const errs = (response.messages.message || []).map(m => m.text).join(' ');
              setStatus('idle');
              setErrorMsg('Card error: ' + errs);
              return;
            }
            const od = response.opaqueData;
            await axios.post(`${backendUrl}/api/payments/charge-card`, {
              invoice_id: invoice.name,
              descriptor: od.dataDescriptor,
              value: od.dataValue,
              tip_amount: tipAmt,
            });
            setStatus('success');
          } catch (e) {
            setStatus('idle');
            setErrorMsg(e.response?.data?.detail || 'Payment failed. Please try again.');
          }
        }
      );
    } catch (e) {
      setStatus('idle');
      setErrorMsg('Payment processor error: ' + (e.message || 'unknown error. Please refresh and try again.'));
    }
  };

  const copyZelle = () => {
    navigator.clipboard.writeText('aaairrigation');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const overlay = {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: 16, overflowY: 'auto',
  };
  const dialog = {
    background: '#1a1f2e', borderRadius: 12,
    width: '100%', maxWidth: 480,
    boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
    overflow: 'hidden', margin: 'auto',
  };
  const header = {
    background: '#1b7abf', color: '#fff',
    padding: '16px 20px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={dialog}>
        {/* Header */}
        <div style={header}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Pay Invoice #{invoice.name}
            </div>
            <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2 }}>
              Amount due: <strong>${invoiceAmt.toFixed(2)}</strong>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* Body */}
        {status === 'success' ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <h3 style={{ color: '#059669', marginBottom: 8 }}>Payment Successful!</h3>
            <p style={{ color: '#555', marginBottom: 20 }}>
              Your payment of <strong>${chargeTotal.toFixed(2)}</strong> for Invoice #{invoice.name} has been processed.
              {tipAmt > 0 && <> Thank you for the <strong>${tipAmt.toFixed(2)}</strong> tip!</>}
            </p>
            <button onClick={() => { if (onSuccess) onSuccess(invoice.name); onClose(); }}
              style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 24px', fontWeight: 700, cursor: 'pointer' }}>
              Done
            </button>
            <p style={{ color: '#aaa', fontSize: 11, marginTop: 16, fontStyle: 'italic' }}>Powered by Boatman Systems™</p>
          </div>

        ) : !method ? (
          /* Method picker */
          <div style={{ padding: '24px 24px 20px' }}>
            <p style={{ color: '#cbd5e1', marginBottom: 16, fontSize: 14 }}>Choose how you'd like to pay:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button onClick={() => setMethod('card')}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', border: '2px solid #334155', borderRadius: 10, background: '#0f172a', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg,#ea580c,#f97316)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#fff', fontSize: 20 }}>💳</span>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9' }}>Credit / Debit Card</div>
                  <div style={{ fontSize: 13, color: '#94a3b8' }}>Secure card payment — processed by Authorize.net</div>
                </div>
              </button>
              <button onClick={() => setMethod('zelle')}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', border: '2px solid #334155', borderRadius: 10, background: '#0f172a', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#6D1ED4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#fff', fontSize: 18, fontWeight: 700 }}>Z</span>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9' }}>Zelle</div>
                  <div style={{ fontSize: 13, color: '#a78bfa', fontWeight: 600 }}>@aaairrigation</div>
                </div>
              </button>
            </div>
          </div>

        ) : method === 'zelle' ? (
          /* Zelle */
          <div style={{ padding: 24 }}>
            <button onClick={() => setMethod(null)}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0 }}>
              ← Back
            </button>
            <div style={{ background: '#0f172a', border: '2px solid #6D1ED4', borderRadius: 10, padding: 20, textAlign: 'center' }}>
              <p style={{ fontWeight: 700, fontSize: 20, color: '#f1f5f9', marginBottom: 4 }}>Send Zelle Payment</p>
              <p style={{ color: '#94a3b8', fontSize: 16, marginBottom: 16 }}>
                Scan with your banking app to send <strong>${invoiceAmt.toFixed(2)}</strong>
              </p>
              <div style={{ background: '#fff', borderRadius: 10, padding: 16, display: 'inline-block', marginBottom: 16, border: '2px solid #6D1ED4' }}>
                <img src="/zelle.jpg" alt="Zelle QR Code" style={{ width: 380, height: 380, display: 'block' }} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <p style={{ fontSize: 15, color: '#94a3b8', margin: '0 0 8px' }}>Or send manually to Zelle tag:</p>
                <div style={{ background: '#1e293b', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #334155' }}>
                  <span style={{ fontWeight: 700, color: '#a78bfa', fontSize: 18 }}>@aaairrigation</span>
                  <button onClick={copyZelle}
                    style={{ background: '#6D1ED4', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', marginLeft: 8 }}>
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              <p style={{ fontSize: 15, color: '#94a3b8', margin: 0 }}>
                Use memo: <strong>{invoice.name}</strong> — ${invoiceAmt.toFixed(2)}<br />
                Questions? Call <strong>(469) 751-3567</strong>
              </p>
            </div>
            <button onClick={onClose}
              style={{ marginTop: 16, width: '100%', background: '#059669', color: '#fff', border: 'none', borderRadius: 8, padding: 12, fontWeight: 700, cursor: 'pointer', fontSize: 15 }}>
              Done — I've sent the payment
            </button>
          </div>

        ) : (
          /* Card — Accept.js form */
          <div style={{ padding: 24 }}>
            <button onClick={() => { setMethod(null); setErrorMsg(''); setStatus('idle'); }}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0 }}>
              ← Back
            </button>

            {!cfg && !errorMsg && (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8' }}>
                <div style={{ margin: '0 auto 10px', width: 28, height: 28, border: '3px solid #334155', borderTopColor: '#ea580c', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                <p style={{ fontSize: 13 }}>Loading payment form…</p>
              </div>
            )}
            {!window.Accept && cfg && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 14 }}>
                Payment processor script failed to load. Please disable any ad blockers or try a different browser, then refresh.
              </div>
            )}

            {errorMsg && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 14 }}>
                {errorMsg}
              </div>
            )}

            {cfg && (
              <>
                {/* Card fields */}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>Card Number</label>
                  <input
                    type="tel" inputMode="numeric" maxLength={19}
                    placeholder="•••• •••• •••• ••••"
                    value={cardNum}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, '').substring(0, 16);
                      setCardNum(v.replace(/(.{4})/g, '$1 ').trim());
                    }}
                    style={{ width: '100%', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 15, boxSizing: 'border-box', background: '#0f172a', color: '#f1f5f9' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>Exp Month</label>
                    <input type="tel" maxLength={2} placeholder="MM" value={expMonth} onChange={e => setExpMonth(e.target.value.replace(/\D/g, ''))}
                      style={{ width: '100%', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 15, boxSizing: 'border-box', background: '#0f172a', color: '#f1f5f9' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>Exp Year</label>
                    <input type="tel" maxLength={4} placeholder="YYYY" value={expYear} onChange={e => setExpYear(e.target.value.replace(/\D/g, ''))}
                      style={{ width: '100%', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 15, boxSizing: 'border-box', background: '#0f172a', color: '#f1f5f9' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>CVV</label>
                    <input type="tel" maxLength={4} placeholder="•••" value={cvv} onChange={e => setCvv(e.target.value.replace(/\D/g, ''))}
                      style={{ width: '100%', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 15, boxSizing: 'border-box', background: '#0f172a', color: '#f1f5f9' }} />
                  </div>
                </div>

                {/* Tip selector */}
                <div style={{ borderTop: '1px solid #334155', paddingTop: 14, marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 10 }}>Add a Tip (Optional)</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    {[['none','No Tip'],['15','15%'],['20','20%'],['custom','Custom']].map(([val, label]) => (
                      <button key={val} onClick={() => setTipMode(val)}
                        style={{
                          padding: '7px 16px', borderRadius: 20, fontSize: 13, cursor: 'pointer', fontWeight: tipMode === val ? 700 : 400,
                          border: tipMode === val ? '2px solid #ea580c' : '1px solid #334155',
                          background: tipMode === val ? '#431407' : '#0f172a',
                          color: tipMode === val ? '#fb923c' : '#94a3b8',
                        }}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {tipMode === 'custom' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: '#e2e8f0', fontWeight: 600 }}>$</span>
                      <input type="number" min="0" step="0.01" placeholder="0.00" value={customTip}
                        onChange={e => setCustomTip(e.target.value)}
                        style={{ width: 120, border: '1px solid #334155', borderRadius: 6, padding: '7px 10px', fontSize: 14, background: '#0f172a', color: '#f1f5f9' }} />
                    </div>
                  )}
                </div>

                {/* Charge summary */}
                <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#cbd5e1' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Invoice</span><span>${invoiceAmt.toFixed(2)}</span>
                  </div>
                  {tipAmt > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Tip</span><span>${tipAmt.toFixed(2)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, marginTop: 6, borderTop: '1px solid #334155', paddingTop: 6 }}>
                    <span>Total</span><span>${chargeTotal.toFixed(2)}</span>
                  </div>
                </div>

                <button onClick={handleCharge} disabled={status === 'processing'}
                  style={{ width: '100%', background: status === 'processing' ? '#93c5fd' : '#1b7abf', color: '#fff', border: 'none', borderRadius: 8, padding: '13px', fontWeight: 700, fontSize: 16, cursor: status === 'processing' ? 'not-allowed' : 'pointer' }}>
                  {status === 'processing' ? 'Processing…' : `Charge $${chargeTotal.toFixed(2)}`}
                </button>

                <p style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', marginTop: 10 }}>
                  🔒 Card info is encrypted by Authorize.net — never touches our server
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
